// SearchLocateBar.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    Paper,
    Fade,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Autocomplete,
    Checkbox,
    Chip,
    Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ClearIcon from "@mui/icons-material/Clear";
import FilterIcon from "@mui/icons-material/FilterAlt";

import { marginFab, zIndexSearchBar } from "./helpers";
import GroupChip from "./GroupChip";

const DEBOUNCE_MS = 380;
export const MIN_CHARS = 3;

const MIN_OPACITY = 0.25;
const MAX_OPACITY = 0.85;

const EMPTY_RULES = { include: { groups: [] }, exclude: { groups: [] } };

const FilterRulesButton = ({
    grupos = [],
    initialRules = { include: { groups: [] }, exclude: { groups: [] } }, // comment: controlled seed from parent
    onChange = () => {}, // comment: (rules) => void
    size = "small", // comment: icon size passthrough
}) => {
    // comment: local dialog state
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(0); // comment: 0=Include, 1=Exclude

    // comment: local selections as full group objects, synced from initialRules
    const [includeGroups, setIncludeGroups] = useState([]);
    const [excludeGroups, setExcludeGroups] = useState([]);

    // comment: sync when initialRules change (external reset, etc.)
    useEffect(() => {
        const toObjects = (ids) => {
            const set = new Set((ids || []).map(String));
            return (grupos || []).filter((g) => set.has(String(g.grupo_id)));
        };
        setIncludeGroups(toObjects(initialRules?.include?.groups || []));
        setExcludeGroups(toObjects(initialRules?.exclude?.groups || []));
    }, [initialRules, grupos]);

    const rules = useMemo(() => {
        const toIds = (arr) => arr.map((g) => String(g.grupo_id));
        return {
            include: { groups: toIds(includeGroups) },
            exclude: { groups: toIds(excludeGroups) },
        };
    }, [includeGroups, excludeGroups]);

    const hasActiveFilters =
        (rules.include.groups?.length || 0) > 0 ||
        (rules.exclude.groups?.length || 0) > 0;

    const apply = () => {
        onChange(rules);
        setOpen(false);
    };

    const clear = () => {
        setIncludeGroups([]);
        setExcludeGroups([]);
        onChange({ include: { groups: [] }, exclude: { groups: [] } });
    };

    return (
        <>
            <Tooltip title="Filtros">
                <span>
                    <IconButton
                        size={size}
                        onClick={() => setOpen(true)}
                        aria-label="filters"
                    >
                        <Badge
                            variant="dot"
                            color="primary"
                            invisible={!hasActiveFilters}
                        >
                            <FilterIcon fontSize="small" />
                        </Badge>
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Filtros</DialogTitle>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="fullWidth"
                >
                    <Tab label="Incluir" />
                    <Tab label="Excluir" />
                </Tabs>
                <DialogContent dividers>
                    {tab === 0 && (
                        <Autocomplete
                            multiple
                            options={grupos}
                            value={includeGroups}
                            onChange={(_, val) => setIncludeGroups(val || [])}
                            getOptionLabel={(o) => o?.nombre || ""}
                            isOptionEqualToValue={(o, v) =>
                                String(o.grupo_id) === String(v?.grupo_id)
                            }
                            renderInput={(p) => (
                                <TextField
                                    {...p}
                                    label="Grupos a incluir"
                                    size="small"
                                    placeholder="Elegir…"
                                />
                            )}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={String(option.grupo_id)}>
                                    <Checkbox size="small" checked={selected} />
                                    {option?.icon ? `${option.icon} ` : ""}
                                    {option?.nombre}
                                </li>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((opt, i) => (
                                    <GroupChip
                                        {...getTagProps({ index: i })} // comment: keeps delete behavior from Autocomplete
                                        key={String(opt.grupo_id)}
                                        nombre={opt?.nombre}
                                        icon={opt?.icon}
                                        color={opt?.color || "#999999"} // comment: fallback color
                                        size="small"
                                        // variant="outlined"
                                        deletable // comment: show delete (x)
                                    />
                                ))
                            }
                        />
                    )}
                    {tab === 1 && (
                        <Autocomplete
                            multiple
                            options={grupos}
                            value={excludeGroups}
                            onChange={(_, val) => setExcludeGroups(val || [])}
                            getOptionLabel={(o) => o?.nombre || ""}
                            isOptionEqualToValue={(o, v) =>
                                String(o.grupo_id) === String(v?.grupo_id)
                            }
                            renderInput={(p) => (
                                <TextField
                                    {...p}
                                    label="Grupos a excluir"
                                    size="small"
                                    placeholder="Elegir…"
                                />
                            )}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={String(option.grupo_id)}>
                                    <Checkbox size="small" checked={selected} />
                                    {option?.icon ? `${option.icon} ` : ""}
                                    {option?.nombre}
                                </li>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((opt, i) => (
                                    <GroupChip
                                        {...getTagProps({ index: i })} // comment: keeps delete behavior from Autocomplete
                                        key={String(opt.grupo_id)}
                                        nombre={opt?.nombre}
                                        icon={opt?.icon}
                                        color={opt?.color || "#999999"} // comment: fallback color
                                        size="small"
                                        // variant="outlined"
                                        deletable // comment: show delete (x)
                                    />
                                ))
                            }
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={clear}>Limpiar</Button>
                    <Button variant="contained" onClick={apply}>
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const SearchLocateBar = ({
    personas = [],
    relaciones = [],
    grupos = [],
    gruposPersonas = [],
    onLocatePersona, // comment: (id: string) => void  → should select + center + zoom
    onFilterRulesChange = () => {}, // comment: parent callback
    filterRules = { include: { groups: [] }, exclude: { groups: [] } },
}) => {
    const [searchText, setSearchText] = useState("");
    const [hits, setHits] = useState([]); // comment: array of { item, refIndex }
    const [i, setI] = useState(0); // comment: current index within hits
    const tRef = useRef(null);
    const [lastLocatedId, setLastLocatedId] = useState(null);
    const [opacity, setOpacity] = useState(0.35);
    const idleTimeout = useRef(null);

    const gruposById = useMemo(() => {
        // comment: Map<grupo_id string, nombre string>
        const m = new Map();
        (grupos || []).forEach((g) =>
            m.set(String(g.grupo_id), g?.nombre || "")
        );
        return m;
    }, [grupos]);

    const personaToGroupNames = useMemo(() => {
        // comment: Map<persona_id string, string[] group names>
        const m = new Map();
        (gruposPersonas || []).forEach((gp) => {
            const pid = String(gp.persona_id);
            const gid = String(gp.grupo_id);
            const gname = gruposById.get(gid) || "";
            if (!gname) return;
            const arr = m.get(pid) || [];
            // comment: avoid duplicates if any
            if (!arr.includes(gname)) arr.push(gname);
            m.set(pid, arr);
        });
        return m;
    }, [gruposPersonas, gruposById]);

    const items = useMemo(() => {
        // comment: Build searchable entries per persona including relation names (tipo), neighbor display names, and group names
        const byId = new Map();
        personas.forEach((p) => byId.set(String(p.persona_id), p));

        return personas.map((p) => {
            const id = String(p.persona_id);
            const display = p?.display_name || "";

            // comment: collect relations for this persona
            const relations = [];
            relaciones.forEach((r) => {
                const a = String(r.persona_a);
                const b = String(r.persona_b);
                if (a === id || b === id) {
                    const otherId = a === id ? b : a;
                    const otherName = byId.get(otherId)?.display_name || "";
                    const tipo = r?.tipo || "";
                    relations.push({ tipo, otherName });
                }
            });

            // comment: collect group names for this persona
            const groups = personaToGroupNames.get(id) || [];

            return { id, name: display, relations, groups };
        });
    }, [personas, relaciones, personaToGroupNames]);

    const fuse = useMemo(() => {
        return new Fuse(items, {
            includeScore: true,
            threshold: 0.35,
            ignoreLocation: true,
            keys: [
                { name: "name", weight: 0.9 },
                { name: "relations.tipo", weight: 0.4 },
                { name: "relations.otherName", weight: 0.2 },
                { name: "groups", weight: 0.7 },
            ],
        });
    }, [items]);

    // comment: run search reactively with debounce; when user pauses, jump to best match
    useEffect(() => {
        clearTimeout(tRef.current);
        if (!searchText.trim() || searchText.trim().length < MIN_CHARS) {
            setHits([]);
            setI(0);
            setLastLocatedId(null); // reset si se borra la búsqueda
            return;
        }
        tRef.current = setTimeout(() => {
            const res = fuse.search(searchText.trim());

            const mapped = res.map((r) => ({ item: r.item, score: r.score }));

            // si el resultado actual sigue existiendo, mantenemos el índice
            const currentId = hits[i]?.item?.id;
            const newIndex = mapped.findIndex((h) => h.item.id === currentId);
            const nextIndex = newIndex >= 0 ? newIndex : 0;

            setHits(mapped);
            setI(nextIndex);

            const top = mapped[nextIndex]?.item?.id;
            if (top && top !== lastLocatedId && onLocatePersona) {
                onLocatePersona(String(top));
                setLastLocatedId(top);
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(tRef.current);
    }, [searchText, fuse, onLocatePersona, lastLocatedId]);

    // comment: go to specific hit index (wrap around) and center it
    const goTo = (nextIndex) => {
        if (!hits.length) return;
        const idx = ((nextIndex % hits.length) + hits.length) % hits.length;
        setI(idx);
        const id = hits[idx]?.item?.id;
        if (id && onLocatePersona) onLocatePersona(String(id));
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            // comment: Enter → next
            e.preventDefault();
            goTo(i + 1);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            goTo(i - 1);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            goTo(i + 1);
        } else if (e.key === "Escape") {
            setSearchText("");
            setHits([]);
            setI(0);
        }
    };

    const handleFocus = () => {
        clearTimeout(idleTimeout.current);
        setOpacity(0.85);
    };

    const handleBlur = () => {
        if (!searchText) {
            idleTimeout.current = setTimeout(
                () => setOpacity(MIN_OPACITY),
                2500
            );
        }
    };

    useEffect(() => {
        if (!searchText) {
            clearTimeout(idleTimeout.current);
            idleTimeout.current = setTimeout(
                () => setOpacity(MIN_OPACITY),
                2500
            );
        } else {
            setOpacity(MAX_OPACITY);
        }
    }, [searchText]);

    const gruposByIdFull = useMemo(() => {
        const m = new Map();
        (grupos || []).forEach((g) => m.set(String(g.grupo_id), g));
        return m;
    }, [grupos]);

    // comment: derive active include/exclude groups as full objects for rendering
    const includeActive = useMemo(
        () =>
            (filterRules?.include?.groups || [])
                .map((id) => gruposByIdFull.get(String(id)))
                .filter(Boolean),
        [filterRules, gruposByIdFull]
    );

    const excludeActive = useMemo(
        () =>
            (filterRules?.exclude?.groups || [])
                .map((id) => gruposByIdFull.get(String(id)))
                .filter(Boolean),
        [filterRules, gruposByIdFull]
    );

    const hasActiveFilters =
        (filterRules?.include?.groups?.length || 0) > 0 ||
        (filterRules?.exclude?.groups?.length || 0) > 0;

    return (
        <Box
            sx={{
                position: "fixed",
                top: marginFab,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: zIndexSearchBar,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end", // alinea a la derecha dentro del bloque
                gap: 0.5, // separación automática entre el Paper principal y el secundario
            }}
        >
            {/* Paper principal */}
            <Stack direction="row" spacing={1} alignItems="center">
                <Paper
                    elevation={3}
                    onClick={handleFocus}
                    sx={{
                        // p: 1,
                        display: "flex",
                        alignItems: "center",
                        // gap: 1,
                        backgroundColor: `rgba(255, 255, 255, ${opacity})`,
                        transition: "background-color 0.6s ease-in-out",
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Buscar…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={onKeyDown}
                        onBlur={handleBlur}
                        sx={{ maxWidth: 200 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchText ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSearchText("");
                                            setHits([]);
                                            setI(0);
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Paper>
                <Paper
                    sx={{
                        borderRadius: 10,

                        // p: 1,
                        display: "flex",
                        alignItems: "center",
                        // gap: 1,
                        backgroundColor: `rgba(255, 255, 255, ${opacity})`,
                        transition: "background-color 0.6s ease-in-out",
                        height: "100%",
                    }}
                >
                    <FilterRulesButton
                        grupos={grupos}
                        initialRules={filterRules || EMPTY_RULES}
                        onChange={onFilterRulesChange}
                        size="small"
                    />
                </Paper>
            </Stack>

            <Fade in={hasActiveFilters} unmountOnExit>
                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        maxWidth: 560,
                        backgroundColor: `rgba(255, 255, 255, ${opacity})`,
                    }}
                >
                    {/* comment: chips wrap nicely on small screens */}
                    <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ alignItems: "center", flex: 1, minHeight: 30 }}
                    >
                        {includeActive.map((g) => (
                            <Chip
                                key={`inc_${String(g.grupo_id)}`}
                                size="small"
                                variant="outlined"
                                color="success" // comment: include → green-ish
                                label={`${g?.icon ? g.icon + " " : ""}${
                                    g?.nombre ?? ""
                                }`}
                            />
                        ))}
                        {excludeActive.map((g) => (
                            <Chip
                                key={`exc_${String(g.grupo_id)}`}
                                size="small"
                                variant="filled"
                                color="error" // comment: exclude → red-ish
                                label={`${g?.icon ? g.icon + " " : ""}${
                                    g?.nombre ?? ""
                                }`}
                            />
                        ))}
                    </Stack>

                    {/* comment: quick clear button */}
                    <Tooltip title="Limpiar filtros">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    onFilterRulesChange({
                                        include: { groups: [] },
                                        exclude: { groups: [] },
                                    })
                                }
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Paper>
            </Fade>

            {/* Paper secundario */}
            <Fade
                in={searchText.trim().length >= MIN_CHARS && hits.length > 0}
                unmountOnExit
            >
                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: 10,
                        // p: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        // minWidth: 120,
                        backgroundColor: `rgba(255, 255, 255, ${opacity})`,
                    }}
                >
                    <Tooltip title="Anterior (↑)">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => goTo(i - 1)}
                                disabled={!hits.length}
                            >
                                <NavigateBeforeIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="body2"
                        sx={{
                            minWidth: 48,
                            textAlign: "center",
                            opacity: opacity,
                            transition: "opacity 0.6s ease-in-out",
                        }}
                    >
                        {`${i + 1} / ${hits.length}`}
                    </Typography>
                    <Tooltip title="Siguiente (↓ o Enter)">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => goTo(i + 1)}
                                disabled={!hits.length}
                            >
                                <NavigateNextIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Paper>
            </Fade>
        </Box>
    );
};
export default SearchLocateBar;
